import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Film, Star, Users, Compass } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

export default function FestivalFollowing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ follows_count: 0, films: [] });
  const [loading, setLoading] = useState(true);
  const [topFilmmakers, setTopFilmmakers] = useState([]);

  useEffect(() => {
    api.get("/festival/following")
      .then((r) => setData(r.data || { follows_count: 0, films: [] }))
      .catch(() => setData({ follows_count: 0, films: [] }))
      .finally(() => setLoading(false));
    api.get("/festival/filmmakers/top?limit=8")
      .then((r) => setTopFilmmakers(r.data || []))
      .catch(() => setTopFilmmakers([]));
  }, []);

  if (!user) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-32 px-6 text-center text-gray-400 max-w-md mx-auto">
        <Heart className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        Sign in to see films from filmmakers you follow.
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-[1400px] mx-auto pb-20">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-[0.25em] text-amber-400 font-bold">
              <Heart className="w-3.5 h-3.5 fill-amber-400" /> Following
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Your Filmmakers</h1>
            <p data-testid="follows-count" className="text-gray-500 text-sm mt-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Following <strong className="text-white">{data.follows_count}</strong> filmmaker{data.follows_count === 1 ? "" : "s"}
            </p>
          </div>
          <Link to="/festival" className="btn-glass px-5 py-2 rounded font-semibold inline-flex items-center gap-2 border border-white/15">
            <Compass className="w-4 h-4" /> Discover more
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading…</div>
        ) : data.films.length === 0 ? (
          <div>
            <div className="text-center py-12 text-gray-400 bg-[#0f0f0f] border border-dashed border-white/10 rounded-lg mb-10">
              <Heart className="w-12 h-12 mx-auto mb-4 text-amber-400/40" />
              <p className="text-base mb-2">{data.follows_count === 0 ? "You're not following anyone yet." : "Your filmmakers haven't released anything new — yet."}</p>
              <p className="text-sm text-gray-500 mb-6">
                {data.follows_count === 0
                  ? "Tap Follow on any profile below to start, then return here for their drops."
                  : "We'll push a notification the moment they do."}
              </p>
              <Link to="/festival" className="btn-red px-6 py-2.5 rounded font-semibold inline-flex items-center gap-2">
                <Compass className="w-4 h-4" /> Browse the festival
              </Link>
            </div>
            {data.follows_count === 0 && topFilmmakers.length > 0 && (
              <div data-testid="discover-rail">
                <div className="flex items-center gap-2 mb-5">
                  <Compass className="w-5 h-5 text-amber-400" />
                  <h2 className="text-lg font-bold uppercase tracking-widest">Discover Filmmakers</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {topFilmmakers.map((fm) => (
                    <Link
                      key={fm.user_id}
                      data-testid={`discover-fm-${fm.user_id}`}
                      to={`/filmmaker/${fm.user_id}`}
                      className="group bg-[#141414] border border-white/5 rounded-lg p-5 hover:border-amber-500/40 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        {fm.picture ? (
                          <img src={fm.picture} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-700/40 to-[#E50914]/40 flex items-center justify-center text-sm font-black flex-shrink-0">
                            {(fm.name || "F").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-bold truncate group-hover:text-amber-300 transition-colors">{fm.name || "Filmmaker"}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {fm.country && <span>{fm.country} · </span>}{fm.total_films} film{fm.total_films === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {data.films.map((f) => (
              <Link
                key={f.submission_id}
                data-testid={`following-film-${f.submission_id}`}
                to={`/festival/films/${f.submission_id}`}
                className="group relative aspect-[2/3] rounded overflow-hidden bg-[#141414] hover:scale-[1.04] transition-transform"
              >
                {f.poster_url ? (
                  <img src={f.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-600">
                    <Film className="w-10 h-10" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">{f.filmmaker?.name || "Filmmaker"}</div>
                  <h3 className="text-sm font-bold leading-tight line-clamp-2">{f.title}</h3>
                  <div className="flex items-center gap-1 text-[11px] text-gray-300 mt-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span className="font-bold">{(f.score || 0).toFixed(2)}</span>
                    <span className="text-gray-500">· {f.total_votes || 0}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
